import React, { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import type { Stats } from '../../hooks/useStats';
import { useUtPlayerIndex } from '../../hooks/useUtPlayerIndex';
import { isRanked } from '../../utils/rarityCalculations';
import { YearSelector } from '../../components/stats/YearSelector';
import { LiveStatsTab } from '../../components/leaderboards/LiveStatsTab';
import { ComprehensiveStatsTable } from '../../components/stats/ComprehensiveStatsTable';
import { ScreenTitle, Segmented, PlayerRow, TierLegend, UtButton, UtError, UtLoading, UtCard, formatXp, winRateLabel, utTier, WhatsAppMark } from '../../components/ut';

type Tab = 'xp' | 'awards' | 'stats' | 'all';
const TABS: { value: Tab; label: string }[] = [
  { value: 'xp', label: 'XP' },
  // Awards live on /trophies now; the tab stays so mobile can reach them.
  { value: 'awards', label: 'Awards' },
  { value: 'stats', label: 'Stats' },
  { value: 'all', label: 'All' },
];

interface Props {
  selectedYear: number | 'all';
  onYearChange: (y: number | 'all') => void;
  onYearsLoaded: (years: number[]) => void;
  stats: Stats;
}

/**
 * Ultimate Team /leaderboards: XP podium and ranks, Awards grid, the live
 * Stats boards and the All Players table. Leaderboards.tsx owns the year and
 * the stats/awards queries; the tab lives in ?tab= so the top nav can link to
 * Awards directly.
 */
const UtLeaderboards: React.FC<Props> = ({ selectedYear, onYearChange, onYearsLoaded, stats }) => {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((t) => t.value === params.get('tab')) ? params.get('tab') : 'xp') as Tab;
  const navigate = useNavigate();
  const setTab = (t: Tab) => {
    if (t === 'awards') {
      navigate('/trophies');
      return;
    }
    const next = new URLSearchParams(params);
    if (t === 'xp') next.delete('tab');
    else next.set('tab', t);
    setParams(next, { replace: true });
  };
  const { index, loading, error } = useUtPlayerIndex();
  const [showAll, setShowAll] = useState(false);

  const ranked = [...index.values()].filter(isRanked).sort((a, b) => b.xp - a.xp);
  const podium = ranked.slice(0, 3);
  const rest = ranked.slice(3, showAll ? ranked.length : 10);
  const period = selectedYear === 'all' ? 'ALL-TIME' : `${selectedYear} SEASON`;
  const titles: Record<Tab, string> = { xp: 'Leaderboards', awards: 'Awards', stats: 'Stats', all: 'All players' };

  return (
    <section className="flex flex-col gap-[18px]">
      <ScreenTitle
        eyebrow={tab === 'xp' ? 'CURRENT XP' : period}
        title={titles[tab]}
        right={<Segmented options={TABS} value={tab} onChange={setTab} />}
      />
      {tab !== 'xp' && <YearSelector selectedYear={selectedYear} onYearChange={onYearChange} onYearsLoaded={onYearsLoaded} />}

      {tab === 'xp' && (
        <>
          {error && <UtError message={`Couldn't load the table: ${error}`} />}
          {loading && ranked.length === 0 ? (
            <UtLoading label="Loading table" />
          ) : (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,400px),1fr))] items-start gap-[22px]">
              <div className="grid grid-cols-[1fr_1.15fr_1fr] items-end gap-2.5 pt-2">
                {[podium[1], podium[0], podium[2]].map((p, i) =>
                  p ? (
                    <div key={p.id} className="flex flex-col items-center gap-1.5">
                      <UtCard player={p} size={i === 1 ? 'podium-lead' : 'podium'} className="w-full" />
                      <span className={`font-ut-display text-2xl font-extrabold ${i === 1 ? 'text-ut-acc' : ''}`}>{[2, 1, 3][i]}</span>
                      <span className="flex items-center justify-center gap-1 text-center text-xs text-ut-mut">
                        {p.whatsappMember && <WhatsAppMark size={11} />}
                        {p.caps} caps · {winRateLabel(p.winRate, p.caps)}
                      </span>
                    </div>
                  ) : (
                    <div key={i} />
                  ),
                )}
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col overflow-hidden rounded-[14px] bg-ut-card">
                  {rest.map((p, i) => (
                    <PlayerRow
                      key={p.id}
                      to={`/players/${p.id}`}
                      playerId={p.id}
                      name={p.name}
                      nameAdornment={p.whatsappMember ? <WhatsAppMark /> : undefined}
                      rarity={p.rarity}
                      xp={p.xp}
                      className="border-b border-ut-line last:border-0"
                      lead={<span className="w-7 flex-none font-ut-display text-base font-bold text-ut-mut">{i + 4}</span>}
                      sub={[utTier(p.rarity, p.xp).label, p.position, p.run !== '—' ? p.run : null]
                        .filter(Boolean)
                        .join(' · ')}
                      right={<span className="font-ut-display text-lg font-extrabold">{formatXp(p.xp)}</span>}
                    />
                  ))}
                </div>
                {ranked.length > 10 && (
                  <UtButton variant="secondary" size="sm" onClick={() => setShowAll((v) => !v)} className="self-start">
                    {showAll ? 'Show top 10' : `Show all ${ranked.length}`}
                  </UtButton>
                )}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <TierLegend />
            <span className="flex items-center gap-1.5 text-xs text-ut-mut">
              <WhatsAppMark /> In the WNF WhatsApp group
            </span>
          </div>
        </>
      )}

      {tab === 'awards' && <Navigate to="/trophies" replace />}

      {tab === 'stats' &&
        (stats.error ? <UtError message={`Error loading stats: ${stats.error}`} /> : stats.loading ? <UtLoading label="Loading stats" /> : <LiveStatsTab stats={stats} selectedYear={selectedYear} />)}

      {tab === 'all' && <ComprehensiveStatsTable selectedYear={selectedYear} />}
    </section>
  );
};

export default UtLeaderboards;
